import React, { useEffect, useState, useCallback } from "react";
import { Loader2, Search, Download, Phone, Mail, CalendarDays, StickyNote, Bell, X, Trash2 } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { http, downloadBlob } from "@/hooks/useAdmin";
import { fmtDate, sourceLabel } from "@/components/admin/LeadViews";

export const STAGES = [
  { key: "new", label: "New", cls: "bg-royal/10 text-royal border-royal/20" },
  { key: "contacted", label: "Contacted", cls: "bg-gold/15 text-[#8a6500] border-gold/30" },
  { key: "meeting_booked", label: "Meeting booked", cls: "bg-violet-50 text-violet-700 border-violet-200" },
  { key: "converted", label: "Converted", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { key: "lost", label: "Lost", cls: "bg-slate-100 text-slate-600 border-slate-200" },
];
const stageCls = (s) => (STAGES.find((x) => x.key === s) || STAGES[0]).cls;
const today = () => new Date().toISOString().slice(0, 10);
const isDue = (c) => c.follow_up_at && c.follow_up_at.slice(0, 10) <= today() && !["converted", "lost"].includes(c.stage);

export const StageSelect = ({ contact, onChange, testId }) => (
  <select
    data-testid={testId || `contact-stage-${contact.id}`}
    value={contact.stage || "new"}
    onChange={(e) => onChange(contact.id, { stage: e.target.value })}
    className={`cursor-pointer rounded-full border px-3 py-1.5 text-xs font-semibold outline-none focus:ring-2 focus:ring-royal/20 ${stageCls(contact.stage)}`}
  >
    {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
  </select>
);

const Pipeline = ({ counts, total, filter, onFilter }) => (
  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
    {[{ key: "all", label: "All contacts", n: total }, ...STAGES.map((s) => ({ key: s.key, label: s.label, n: counts[s.key] || 0 }))].map((c) => (
      <button key={c.key} data-testid={`contact-filter-${c.key}`} onClick={() => onFilter(c.key)}
        className={`rounded-2xl border bg-white p-4 text-left transition-[transform,box-shadow] hover:-translate-y-0.5 hover:shadow-hover ${filter === c.key ? "border-royal ring-2 ring-royal/15" : "border-navy/[0.08]"}`}>
        <p className="font-display text-xl font-extrabold text-navy" data-testid={`contact-count-${c.key}`}>{c.n}</p>
        <p className="text-xs text-ink/55">{c.label}</p>
      </button>
    ))}
  </div>
);

const ContactRow = ({ c, onOpen, onStage }) => (
  <button data-testid={`contact-row-${c.id}`} onClick={() => onOpen(c.id)}
    className="flex w-full flex-col gap-3 rounded-2xl border border-navy/[0.08] bg-white p-4 text-left transition-[transform,box-shadow] hover:-translate-y-0.5 hover:shadow-hover sm:flex-row sm:items-center sm:justify-between">
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-2">
        <p className="font-display text-base font-bold text-navy">{c.name || "Unnamed"}</p>
        {isDue(c) && <span data-testid={`contact-due-${c.id}`} className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-600"><Bell className="h-3 w-3" /> Follow-up due</span>}
        {(c.tags || []).map((t) => <span key={t} className="rounded-full bg-lightbg px-2 py-0.5 text-[11px] font-medium text-navy/70">#{t}</span>)}
      </div>
      <p className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink/60">
        <span className="flex items-center gap-1"><Phone className="h-3 w-3 text-royal" /> {c.mobile}</span>
        <span className="flex items-center gap-1"><Mail className="h-3 w-3 text-royal" /> {c.email}</span>
      </p>
      <p className="mt-1 text-[11px] text-ink/45">
        {c.lead_count} enquir{c.lead_count === 1 ? "y" : "ies"} · {c.appointment_count} appointment{c.appointment_count === 1 ? "" : "s"} · {c.note_count} note{c.note_count === 1 ? "" : "s"}
        {c.next_appointment && <> · <span className="font-semibold text-navy/70">Next: {c.next_appointment.date} {c.next_appointment.start}</span></>}
      </p>
    </div>
    <div onClick={(e) => e.stopPropagation()} className="shrink-0"><StageSelect contact={c} onChange={onStage} /></div>
  </button>
);

const ContactDetail = ({ id, onClose, onChanged }) => {
  const [c, setC] = useState(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [tagInput, setTagInput] = useState("");

  const load = useCallback(() => http.get(`/admin/contacts/${id}`).then(({ data }) => setC(data)), [id]);
  useEffect(() => { load(); }, [load]);

  const patch = async (body) => {
    const { data } = await http.patch(`/admin/contacts/${id}`, body);
    setC((prev) => ({ ...prev, ...data }));
    onChanged();
  };
  const addNote = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    setSaving(true);
    try {
      await http.post(`/admin/contacts/${id}/notes`, { text: note.trim() });
      setNote("");
      await load();
      onChanged();
    } finally {
      setSaving(false);
    }
  };
  const delNote = async (nid) => { await http.delete(`/admin/contacts/${id}/notes/${nid}`); await load(); onChanged(); };
  const addTag = (e) => {
    e.preventDefault();
    const t = tagInput.trim().replace(/^#/, "");
    if (!t) return;
    patch({ tags: [...(c.tags || []), t] });
    setTagInput("");
  };

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent data-testid="contact-detail" className="w-full overflow-y-auto bg-lightbg sm:max-w-xl">
        {!c ? (
          <>
            <SheetTitle className="sr-only">Loading contact</SheetTitle>
            <SheetDescription className="sr-only">Please wait</SheetDescription>
            <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-navy/40" /></div>
          </>
        ) : (
          <>
            <SheetHeader className="text-left">
              <SheetTitle className="font-display text-2xl font-extrabold text-navy" data-testid="contact-detail-name">{c.name}</SheetTitle>
              <SheetDescription className="text-sm text-ink/60">
                <a href={`tel:${c.mobile}`} className="hover:text-navy">{c.mobile}</a> · <a href={`mailto:${c.email}`} className="hover:text-navy">{c.email}</a> · since {fmtDate(c.created_at)}
              </SheetDescription>
            </SheetHeader>

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <div className="rounded-2xl border border-navy/[0.08] bg-white p-4">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-ink/45">Pipeline stage</p>
                <div className="mt-2"><StageSelect contact={c} onChange={(_, b) => patch(b)} testId="contact-detail-stage" /></div>
              </div>
              <div className="rounded-2xl border border-navy/[0.08] bg-white p-4">
                <label className="text-[11px] font-semibold uppercase tracking-wide text-ink/45" htmlFor="fu">Follow-up reminder</label>
                <div className="mt-2 flex items-center gap-2">
                  <input id="fu" type="date" data-testid="contact-followup" value={c.follow_up_at ? c.follow_up_at.slice(0, 10) : ""} onChange={(e) => patch({ follow_up_at: e.target.value })} className="w-full rounded-lg border border-navy/10 px-2 py-1.5 text-sm text-navy outline-none focus:border-royal" />
                  {c.follow_up_at && <button data-testid="contact-followup-clear" onClick={() => patch({ follow_up_at: "" })} className="text-ink/40 hover:text-red-500" aria-label="Clear"><X className="h-4 w-4" /></button>}
                </div>
                {isDue(c) && <p className="mt-1 text-[11px] font-semibold text-red-600">Due now</p>}
              </div>
            </div>

            <div className="mt-4 rounded-2xl border border-navy/[0.08] bg-white p-4">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-ink/45">Tags</p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {(c.tags || []).map((t) => (
                  <span key={t} className="inline-flex items-center gap-1 rounded-full bg-lightbg px-2.5 py-1 text-xs font-medium text-navy/80">#{t}
                    <button onClick={() => patch({ tags: c.tags.filter((x) => x !== t) })} aria-label={`Remove ${t}`} className="text-ink/40 hover:text-red-500"><X className="h-3 w-3" /></button>
                  </span>
                ))}
                <form onSubmit={addTag} className="flex items-center gap-1">
                  <input data-testid="contact-tag-input" value={tagInput} onChange={(e) => setTagInput(e.target.value)} placeholder="Add tag" className="w-24 rounded-full border border-dashed border-navy/20 px-3 py-1 text-xs outline-none focus:border-royal" />
                </form>
              </div>
            </div>

            <div className="mt-4 rounded-2xl border border-navy/[0.08] bg-white p-4">
              <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-ink/45"><StickyNote className="h-3.5 w-3.5" /> Notes</p>
              <form onSubmit={addNote} className="mt-2 flex gap-2">
                <input data-testid="contact-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note about this contact..." className="flex-1 rounded-xl border border-navy/10 px-3 py-2 text-sm outline-none focus:border-royal" />
                <button data-testid="contact-note-add" disabled={saving || !note.trim()} className="rounded-xl bg-navy px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">Add</button>
              </form>
              <ul className="mt-3 space-y-2" data-testid="contact-notes">
                {[...(c.notes || [])].reverse().map((n) => (
                  <li key={n.id} className="group flex items-start justify-between gap-3 rounded-xl bg-lightbg px-3 py-2">
                    <div><p className="text-sm text-navy">{n.text}</p><p className="mt-0.5 text-[11px] text-ink/45">{fmtDate(n.created_at)}</p></div>
                    <button onClick={() => delNote(n.id)} aria-label="Delete note" className="text-ink/30 opacity-0 transition-opacity hover:text-red-500 group-hover:opacity-100"><Trash2 className="h-4 w-4" /></button>
                  </li>
                ))}
                {!(c.notes || []).length && <li className="text-xs text-ink/45">No notes yet.</li>}
              </ul>
            </div>

            <div className="mt-4 rounded-2xl border border-navy/[0.08] bg-white p-4">
              <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-ink/45"><CalendarDays className="h-3.5 w-3.5" /> Appointments</p>
              <ul className="mt-2 space-y-2" data-testid="contact-appointments">
                {c.appointments.map((a) => (
                  <li key={a.id} className="flex items-center justify-between rounded-xl bg-lightbg px-3 py-2 text-sm">
                    <span className="text-navy">{a.date_label || a.date} · {a.time_label || a.start} · {a.topic}</span>
                    <span className="rounded-full bg-white px-2 py-0.5 text-[11px] font-semibold capitalize text-navy/70">{a.status.replace("_", " ")}</span>
                  </li>
                ))}
                {!c.appointments.length && <li className="text-xs text-ink/45">No appointments yet.</li>}
              </ul>
            </div>

            <div className="mt-4 rounded-2xl border border-navy/[0.08] bg-white p-4">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-ink/45">Enquiries</p>
              <ul className="mt-2 space-y-2">
                {c.leads.map((l) => (
                  <li key={l.id} className="rounded-xl bg-lightbg px-3 py-2 text-sm">
                    <p className="text-navy">{l.primary_goal || "General enquiry"} <span className="text-xs capitalize text-ink/50">· {sourceLabel(l.source)}</span></p>
                    <p className="text-[11px] text-ink/45">{fmtDate(l.created_at)}{l.monthly_investment ? ` · invests ${l.monthly_investment}/mo` : ""}</p>
                  </li>
                ))}
                {!c.leads.length && <li className="text-xs text-ink/45">No form enquiries.</li>}
              </ul>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export const ContactsView = ({ onUnauthorized }) => {
  const [data, setData] = useState({ contacts: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [openId, setOpenId] = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await http.get("/admin/contacts");
      setData(data);
    } catch (err) {
      if (err.response?.status === 401) onUnauthorized();
    } finally {
      setLoading(false);
    }
  }, [onUnauthorized]);
  useEffect(() => { load(); }, [load]);

  const setStage = async (id, body) => {
    setData((d) => ({ ...d, contacts: d.contacts.map((c) => (c.id === id ? { ...c, ...body } : c)) }));
    await http.patch(`/admin/contacts/${id}`, body);
    load();
  };
  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await http.get("/admin/contacts/export.csv", { responseType: "blob" });
      downloadBlob(res.data, `auxieron-contacts-${today()}.csv`);
    } finally {
      setExporting(false);
    }
  };

  const list = data.contacts.filter((c) => {
    if (filter === "due" ? !isDue(c) : filter !== "all" && c.stage !== filter) return false;
    const s = q.trim().toLowerCase();
    return !s || [c.name, c.email, c.mobile, ...(c.tags || [])].some((v) => (v || "").toLowerCase().includes(s));
  });

  return (
    <div>
      <Pipeline counts={data.counts} total={data.contacts.length} filter={filter} onFilter={setFilter} />
      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <p className="text-sm text-ink/55" data-testid="contact-shown">Showing <strong className="text-navy">{list.length}</strong> of {data.contacts.length}</p>
          {data.counts.follow_up_due > 0 && (
            <button data-testid="contact-filter-due" onClick={() => setFilter(filter === "due" ? "all" : "due")} className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${filter === "due" ? "bg-red-600 text-white" : "bg-red-50 text-red-600"}`}>
              <Bell className="h-3 w-3" /> {data.counts.follow_up_due} follow-up{data.counts.follow_up_due === 1 ? "" : "s"} due
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/40" />
            <input data-testid="contact-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, phone, tag..." className="w-full rounded-full border border-navy/10 bg-white py-2.5 pl-10 pr-4 text-sm text-navy outline-none focus:border-royal sm:w-72" />
          </div>
          <button data-testid="contact-export-csv" onClick={exportCsv} disabled={exporting} className="inline-flex h-11 items-center gap-2 rounded-full bg-navy px-4 text-sm font-semibold text-white disabled:opacity-50">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} <span className="hidden sm:inline">CSV</span>
          </button>
        </div>
      </div>
      <div className="mt-6 space-y-3" data-testid="contact-list">
        {loading ? <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-navy/40" /></div>
          : list.length === 0 ? <div className="rounded-2xl border border-navy/[0.08] bg-white py-20 text-center text-sm text-ink/50" data-testid="contacts-empty">No contacts match.</div>
          : list.map((c) => <ContactRow key={c.id} c={c} onOpen={setOpenId} onStage={setStage} />)}
      </div>
      {openId && <ContactDetail id={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </div>
  );
};
